import React, { useState, useEffect, useRef } from "react";

interface Contact {
  Nome?: string;
  Cargo?: string;
  Telefone?: string;
  Email?: string;
  Site?: string;
  Descricao?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:5000";

const App: React.FC = () => {
  const [data, setData] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentHeader, setCurrentHeader] = useState("");
  const [selectedItems, setSelectedItems] = useState<{ columnIndex: number; value: string }[]>([]);
  const [filterLevel, setFilterLevel] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [contactDetails, setContactDetails] = useState<Contact>({});
  const [contactsData, setContactsData] = useState<Contact[]>([]);
  const [selectedContactIndex, setSelectedContactIndex] = useState(0);
  const [showDivider, setShowDivider] = useState(true);
  const [showConsultarButton, setShowConsultarButton] = useState(false);
  const [empresas, setEmpresas] = useState<string[]>([]);  // Novo estado para armazenar as empresas
  
  // Referência para o conteúdo do modal
  const modalContentRef = useRef<HTMLDivElement>(null);
  
  // Carregar dados iniciais
  useEffect(() => {
  fetch(`${API_BASE_URL}/excel-data`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filters: [], search: "" })
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.uniqueValues && data.header) {
          setData(data.uniqueValues);
          setCurrentHeader(data.header[0]);
          
          // Guardar a lista de empresas (assumindo que estão na quarta coluna)
          if (data.fourthColumnValues) {
            setEmpresas(data.fourthColumnValues);
          }
        }
      })
      .catch((error) => console.error("Erro ao buscar dados:", error))
      .finally(() => setLoading(false));
  }, []);

  // Handler para pesquisa
  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setSearch(searchValue);
    
    // Se a pesquisa está vazia, revertemos para os filtros atuais
    if (!searchValue.trim()) {
    fetch(`${API_BASE_URL}/excel-data`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filters: selectedItems })
      })
        .then((response) => response.json())
        .then((data) => {
          updateDataBasedOnFilterLevel(data);
        })
        .catch((error) => console.error("Erro ao buscar dados:", error));
      return;
    }
    
    // Buscar especificamente empresas que correspondem à pesquisa
    setLoading(true);
  fetch(`${API_BASE_URL}/excel-data`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ 
        filters: selectedItems, 
        search: searchValue,
        searchColumn: 3,  // Forçar a pesquisa na coluna de empresas (índice 3)
        getCompaniesOnly: true  // Nova flag para indicar que queremos apenas empresas
      })
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.searchResults) {
          setData(data.searchResults);
        } else {
          setData([]);
        }
      })
      .catch((error) => console.error("Erro ao buscar resultados da pesquisa:", error))
      .finally(() => setLoading(false));
  };

  interface ExcelData {
    header?: string[];
    uniqueValues?: string[];
    secondColumnValues?: string[];
    thirdColumnValues?: string[];
    fourthColumnValues?: string[];
    fifthColumnValues?: string[];
    sixthColumnValues?: string[];
    searchResults?: string[];
    contactDetails?: Contact[];
  }

  // Helper para atualizar dados com base no nível do filtro
  const updateDataBasedOnFilterLevel = (data: ExcelData) => {
    if (selectedItems.length === 0) {
      setData(data.uniqueValues || []);
      setCurrentHeader(data.header ? data.header[0] : "");
      setShowDivider(true);
      setShowConsultarButton(false);
    } else if (selectedItems.length === 1) {
      setData(data.secondColumnValues || []);
      setCurrentHeader(data.header ? data.header[1] : "");
      setShowDivider(true);
      setShowConsultarButton(false);
    } else if (selectedItems.length === 2) {
      setData(data.thirdColumnValues || []);
      setCurrentHeader(data.header ? data.header[2] : "");
      setShowDivider(true);
      setShowConsultarButton(false);
    } else if (selectedItems.length === 3) {
      setData(data.fourthColumnValues || []);
      setCurrentHeader(data.header ? data.header[3] : "");
      setShowDivider(true);
      setShowConsultarButton(false);
    } else if (selectedItems.length === 4) {
      setData(data.fifthColumnValues || []);
      setCurrentHeader(data.header ? data.header[4] : "");
      setShowDivider(true);
      setShowConsultarButton(false);
    } else if (selectedItems.length === 5) {
      setData(data.sixthColumnValues || []);
      setCurrentHeader("Contatos");
      setShowDivider(false);
      setShowConsultarButton(true);
    }
  };
  
  const fetchExcelData = (newFilter: { columnIndex: number; value: string }) => {
    const newFilters = [...selectedItems, newFilter];
    console.log(newFilters, newFilter.value);

    fetch(`${API_BASE_URL}/excel-data`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filters: newFilters }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (filterLevel === 1 && data.secondColumnValues) {
          setData(data.secondColumnValues);
          setCurrentHeader(data.header[1]);
          setFilterLevel(2);
          setShowConsultarButton(false);
        } else if (filterLevel === 2 && data.thirdColumnValues) {
          setData(data.thirdColumnValues);
          setCurrentHeader(data.header[2]);
          setFilterLevel(3);
          setShowConsultarButton(false);
        } else if (filterLevel === 3 && data.fourthColumnValues) {
          setData(data.fourthColumnValues);
          setCurrentHeader(data.header[3]);
          setFilterLevel(4);
          setShowConsultarButton(false);
        } else if (filterLevel === 4 && data.fifthColumnValues) {
          setData(data.fifthColumnValues);
          setCurrentHeader(data.header[4]);
          setFilterLevel(5);
          setShowConsultarButton(false);
        } else if (filterLevel === 5 && data.sixthColumnValues) {
          setData(data.sixthColumnValues);
          setCurrentHeader("Contatos");
          setShowDivider(false);
          // Aqui ativamos o botão Consultar quando chegamos no nível de Contatos
          setShowConsultarButton(true);
        } 
      })
      .catch((error) => console.error("Erro ao buscar dados filtrados:", error));
  };

  // Nova função para lidar com a seleção de empresa da pesquisa
const handleCompanySelection = (companyName: string) => {
  // Limpar a pesquisa
  setSearch("");
  
  // Criar um novo filtro para a empresa (assumindo que está no nível 3)
  const newFilter = { columnIndex: 3, value: companyName };
  
  // Configurar o estado para esse nível
  setSelectedItems([newFilter]);
  setFilterLevel(4); // Próximo nível após a empresa
  
  // Buscar o próximo nível de dados
  fetch("http://localhost:5000/excel-data", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filters: [newFilter] }),
  })
    .then((response) => response.json())
    .then((data) => {
      // Definir os dados do quinto nível (após a empresa)
      if (data.fifthColumnValues) {
        setData(data.fifthColumnValues);
        setCurrentHeader(data.header[4]);
        setShowDivider(true);
        setShowConsultarButton(false);
      }
    })
    .catch((error) => console.error("Erro ao buscar dados após seleção de empresa:", error));
};

  const handleTagClick = (item: string) => {
    // Se estiver fazendo uma pesquisa por empresa, tratamos de forma especial
    if (search.trim() !== "") {
      // Vamos criar um fluxo específico para empresas da pesquisa
      handleCompanySelection(item);
      return;
    }
    
    // Resto do código existente para o fluxo normal sem pesquisa...
    // Verificar se estamos no nível de contatos (sixthColumnValues)
    if (filterLevel === 6 || currentHeader === "Contatos") {
      // Código existente...
      // Verificar se já temos algum contato selecionado
      const contactIndex = selectedItems.findIndex(
        selectedItem => selectedItem.columnIndex === 5
      );
      
      // Se já temos um contato selecionado, removemos ele primeiro
      if (contactIndex !== -1) {
        const updatedFilters = [...selectedItems];
        updatedFilters.splice(contactIndex, 1);
        setSelectedItems(updatedFilters);
      }
      
      // Adicionar o novo contato selecionado
      const newFilter = { columnIndex: 5, value: item };
      setSelectedItems(prev => [...prev, newFilter]);
      setShowConsultarButton(true);
      return;  // Importante: retornar aqui para não executar o código abaixo
    }
    
    // Fluxo normal
    const newFilter = { columnIndex: selectedItems.length, value: item };
    setSelectedItems((prev) => [...prev, newFilter]);
    fetchExcelData(newFilter);

  };

  const handleSelectedTagClick = (item: string) => {
    const indexToRemove = selectedItems.findIndex((filter) => filter.value === item);
    
    if (indexToRemove === -1) return;

    const updatedFilters = selectedItems.slice(0, indexToRemove);
    setSelectedItems(updatedFilters);
    setFilterLevel(updatedFilters.length + 1);

    fetch(`${API_BASE_URL}/excel-data`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filters: updatedFilters }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (updatedFilters.length === 0) {
          setData(data.uniqueValues);
          setCurrentHeader(data.header[0]);
          setShowDivider(true);
          setShowConsultarButton(false);
        } else if (updatedFilters.length === 1) {
          setData(data.secondColumnValues);
          setCurrentHeader(data.header[1]);
          setShowDivider(true);
          setShowConsultarButton(false);
        } else if (updatedFilters.length === 2) {
          setData(data.thirdColumnValues);
          setCurrentHeader(data.header[2]);
          setShowDivider(true);
          setShowConsultarButton(false);
        } else if (updatedFilters.length === 3) {
          setData(data.fourthColumnValues);
          setCurrentHeader(data.header[3]);
          setShowDivider(true);
          setShowConsultarButton(false);
        } else if (updatedFilters.length === 4) {
          setData(data.fifthColumnValues);
          setCurrentHeader(data.header[4]);
          setShowDivider(true);
          setShowConsultarButton(false);
        } else if (updatedFilters.length === 5) {
          setData(data.sixthColumnValues);
          setCurrentHeader("Contatos");
          setShowDivider(true);
          // Ativa o botão Consultar quando ainda estamos no último nível "Contatos"
          setShowConsultarButton(true);
        } else if (updatedFilters.length === 6) {
          setData([]);
          setCurrentHeader("Contatos");
          setShowConsultarButton(true);
        }
      })
      .catch((error) => console.error("Erro ao buscar dados filtrados:", error));
  };

  const handleConsultar = () => {
    // Aqui buscaremos os detalhes de contato com base nos filtros selecionados
    fetch(`${API_BASE_URL}/excel-data`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filters: selectedItems, getContactDetails: true }),
    })
      .then((response) => response.json())
      .then((data) => {
        // Verifica se o backend retornou os detalhes do contato
        if (data.contactDetails && data.contactDetails.length > 0) {
          setContactsData(data.contactDetails);
          setContactDetails(data.contactDetails[0]);
          setSelectedContactIndex(0);
          setShowModal(true);
        } else {
          // Caso não haja dados, exibimos uma mensagem
          alert("Não foram encontrados contatos para os filtros selecionados.");
        }
      })
      .catch((error) => {
        console.error("Erro ao buscar detalhes do contato:", error);
        alert("Ocorreu um erro ao buscar os detalhes do contato.");
      });
  };

  const nextContact = () => {
    if (contactsData.length > selectedContactIndex + 1) {
      setSelectedContactIndex(selectedContactIndex + 1);
      setContactDetails(contactsData[selectedContactIndex + 1]);
    }
  };

  const prevContact = () => {
    if (selectedContactIndex > 0) {
      setSelectedContactIndex(selectedContactIndex - 1);
      setContactDetails(contactsData[selectedContactIndex - 1]);
    }
  };
  
  // Função para fechar o modal quando clicar fora dele
  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
      setShowModal(false);
    }
  };

  // Verificar se já existe um contato selecionado (para desabilitar todos os outros contatos)
  const hasContactSelected = () => {
    return selectedItems.some(item => item.columnIndex === 5);
  };

  return (
    <div className="main-container">
      <div className="container-first">
        <h1 className="title">{currentHeader}</h1>
        <input
          type="text"
          placeholder="Pesquisar por empresa"
          value={search}
          onChange={handleSearchInput}
          className="search-bar"
        />
        

        {loading ? (
          <p>Carregando...</p>
        ) : data.length > 0 ? (
          <ul className="data-list">
            {data.map((item, index) => {
              // Se estamos no nível de contatos e já existe um contato selecionado,
              // desabilitamos todos os outros botões de contato
              const isContactLevel = currentHeader === "Contatos";
              const contactAlreadySelected = hasContactSelected();
              const isThisContactSelected = selectedItems.some(
                selectedItem => selectedItem.value === item && selectedItem.columnIndex === 5
              );
              
              // Desabilitar se (estamos no nível de contatos E já tem um contato selecionado E não é este contato)
              const isDisabled = isContactLevel && contactAlreadySelected && !isThisContactSelected;
              
              return (
                <button 
                  key={index} 
                  className={`data-item ${isDisabled ? 'disabled-button' : ''}`} 
                  onClick={() => !isDisabled && handleTagClick(item)}
                  disabled={isDisabled}
                >
                  {item}
                </button>
              );
            })}
          </ul>
        ) : (
          search ? <p>Nenhuma empresa encontrada para &ldquo;{search}&rdquo;</p> : <p>Nenhum item disponível</p>
        )}
      </div>

      <div className="container-second">
        <h2>Tags Selecionadas</h2>
        <div className="divider-thr"></div>
        <ul className="selected-tags">
          {selectedItems.map((item, index) => (
            <button key={index} className="tag-item" onClick={() => handleSelectedTagClick(item.value)}>
              {item.value}
            </button>
          ))}
        </ul>
        
        {/* Botão Consultar no container-second - aparece quando chega em "Contatos" */}
        {showConsultarButton && (
          <div className="consultar-button-container">
            <button className="consultar-button" onClick={handleConsultar}>
              Consultar
            </button>
          </div>
        )}
      </div>

      {/* Modal de detalhes do contato */}
      {showModal && (
        <div className="modal-overlay" onClick={handleOutsideClick}>
          <div className="modal-content" ref={modalContentRef}>
            <button className="close-modal-btn" onClick={() => setShowModal(false)}>
              ✕
            </button>
            <h2><strong><span>{contactDetails.Nome || "Não informado"}</span></strong></h2>
            <div className="contact-details">
              <div className="contact-field">
                <strong>Cargo:</strong> <span>{contactDetails.Cargo || "Não informado"}</span>
              </div>
              <div className="contact-field">
                <strong>Telefone:</strong> <span>{contactDetails.Telefone || "Não informado"}</span>
              </div>
              <div className="contact-field">
                <strong>E-mail:</strong> <span>{contactDetails.Email || "Não informado"}</span>
              </div>
              <div className="contact-field">
                <strong>Site:</strong>{" "}
                {contactDetails.Site ? (
                  <a 
                    href={contactDetails.Site.startsWith('http') ? contactDetails.Site : `https://${contactDetails.Site}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    {contactDetails.Site}
                  </a>
                ) : (
                  <span>Não informado</span>
                )}
              </div>
            </div>
            
            {/* Navegação entre contatos se houver mais de um */}
            {contactsData.length > 1 && (
              <div className="contact-navigation">
                <div className="navigation-buttons">
                  <button 
                    className="nav-button" 
                    onClick={prevContact}
                    disabled={selectedContactIndex === 0}
                  >
                    &larr;
                  </button>
                  <button 
                    className="nav-button" 
                    onClick={nextContact}
                    disabled={selectedContactIndex === contactsData.length - 1}
                  >
                    &rarr;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Adicione este estilo no seu CSS */}
      <style>{`
        .disabled-button {
          opacity: 0.5;
          cursor: not-allowed;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

export default App;